import { useState } from "react";
import AppShell from "@/components/AppShell";
import TeacherClassPicker from "@/components/TeacherClassPicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { BookOpen } from "lucide-react";
import {
  classKey,
  classLabelFromKey,
  getSessionUser,
  getSingleFileForClass,
  saveSingleFileForClass,
} from "@/lib/eisStore";
import { decodeClassKey, isSupabaseConfigured, uploadDocument } from "@/lib/supabaseOps";
import { useSupabaseFileUrl } from "@/hooks/useSupabaseFileUrl";

function SyllabusCard({ title, className, payload }) {
  const payloadUrl = useSupabaseFileUrl(payload, "class-files");
  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ color: "#0b1f7a" }}>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-500">{className}</p>
        {payloadUrl ? (
          <div className="space-y-3">
            <a
              href={payloadUrl}
              download={payload.fileName || "syllabus.pdf"}
              className="inline-flex rounded-full px-4 py-2 text-white font-semibold"
              style={{ backgroundColor: "#0b1f7a" }}
            >
              Download PDF
            </a>
            <iframe
              title={title}
              src={payloadUrl}
              className="w-full h-[70vh] rounded-xl border border-slate-200"
            />
          </div>
        ) : (
          <div className="p-12 flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed border-slate-300">
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: "#EEF0FA" }}
            >
              <BookOpen size={28} style={{ color: "#0b1f7a" }} />
            </div>
            <p data-testid="syllabus-empty" className="text-lg font-semibold text-slate-700">
              No syllabus uploaded yet
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StudentSyllabus({ user }) {
  const studentClassKey = classKey(user.standard, user.division);
  const payload = getSingleFileForClass("syllabuses", studentClassKey);

  return (
    <AppShell>
      <div data-testid="syllabus-page" className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
          Syllabus
        </h2>
        <SyllabusCard
          title="Class Syllabus"
          className={classLabelFromKey(studentClassKey)}
          payload={payload}
        />
      </div>
    </AppShell>
  );
}

function TeacherSyllabus({ user }) {
  const [teacherClassKey, setTeacherClassKey] = useState(user.assignedClassKey || "");
  const [uploading, setUploading] = useState(false);
  const payload = teacherClassKey ? getSingleFileForClass("syllabuses", teacherClassKey) : null;

  const upload = async (file) => {
    if (!teacherClassKey) {
      toast.error("Please select a class first");
      return;
    }
    if (!file) {
      toast.error("Please choose a PDF");
      return;
    }

    setUploading(true);
    try {
      const { standard, division } = decodeClassKey(teacherClassKey);
      if (isSupabaseConfigured) {
        try {
          const result = await uploadDocument(file, standard, division, "syllabus", "Syllabus");
          saveSingleFileForClass("syllabuses", teacherClassKey, {
            fileName: file.name,
            filePath: result?.path || undefined,
            bucket: "class-files",
            updatedAt: new Date().toISOString(),
          });
          toast.success("Syllabus uploaded");
          return;
        } catch (error) {
          console.error(error);
        }
      }
      saveSingleFileForClass("syllabuses", teacherClassKey, {
        fileName: file.name,
        filePath: undefined,
        bucket: "class-files",
        updatedAt: new Date().toISOString(),
      });
      toast.success("Syllabus uploaded");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AppShell>
      <div data-testid="syllabus-page" className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
          Syllabus
        </h2>
        <TeacherClassPicker teacher={user} onChange={setTeacherClassKey} />

        <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Upload PDF</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => upload(e.target.files?.[0])}
                className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-full file:border-0 file:bg-[#0b1f7a] file:px-4 file:py-2 file:text-white hover:file:opacity-90"
              />
              {uploading && <p className="text-sm text-slate-500">Uploading...</p>}
              {payload?.fileName && (
                <div className="rounded-xl border border-slate-200 p-4 text-sm text-slate-700">
                  Current file: <span className="font-semibold">{payload.fileName}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <SyllabusCard
            title="Class Syllabus"
            className={teacherClassKey ? classLabelFromKey(teacherClassKey) : "Select a class first"}
            payload={payload}
          />
        </div>
      </div>
    </AppShell>
  );
}

export default function Syllabus() {
  const user = getSessionUser();
  if (user?.role === "teacher") {
    return <TeacherSyllabus user={user} />;
  }
  return <StudentSyllabus user={user || {}} />;
}
